import {
  AlignmentType,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  TextRun,
} from 'docx';
import { marked } from 'marked';

const MAX_IMAGE_WIDTH_PX = 600;

/** Steps that are never part of the principal Word export */
const NON_PRINCIPAL_STEP_IDS = new Set(['start', 'feedback', 'download']);

function stepHaystack(step) {
  return `${step.label || ''} ${step.id || ''}`;
}

function getStepRaw(step, allStepContents) {
  const fromStep = String(step.message ?? '').trim();
  const fromMap = String(allStepContents?.[step.id] ?? '').trim();
  return fromStep || fromMap;
}

function pickSeparata(steps) {
  return steps.find(
    (s) => !NON_PRINCIPAL_STEP_IDS.has(s.id) && /separata/i.test(stepHaystack(s))
  );
}

function pickNewsletter(steps) {
  return steps.find(
    (s) => !NON_PRINCIPAL_STEP_IDS.has(s.id) && /newsletter/i.test(stepHaystack(s))
  );
}

/** Separata workflow: “Content Creator MIA” (label usually has no “separata” substring). */
function pickContentCreator(steps) {
  return steps.find(
    (s) =>
      !NON_PRINCIPAL_STEP_IDS.has(s.id) && /content\s*creator/i.test(stepHaystack(s))
  );
}

/** Whatsapp workflow: copy for HCP after newsletter. */
function pickWhatsappHcpMessage(steps) {
  return steps.find((s) => {
    if (NON_PRINCIPAL_STEP_IDS.has(s.id)) return false;
    const h = stepHaystack(s);
    return (
      /text\s+to\s+whatsapp/i.test(h) ||
      /whatsapp.*\bhcp\b/i.test(h) ||
      /\bhcp\b.*whatsapp/i.test(h)
    );
  });
}

function pickFirstProcessStep(steps) {
  return steps.find((s) => !NON_PRINCIPAL_STEP_IDS.has(s.id));
}

function dedupeStepsById(stepList) {
  const seen = new Set();
  return stepList.filter(Boolean).filter((s) => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
}

/** E-mail after compliance: prefer “email compl…”, else “Content AE…” (template). */
function pickEmailPostCompliance(steps) {
  const cand = steps.filter((s) => !NON_PRINCIPAL_STEP_IDS.has(s.id));
  const compl = cand.find((s) => /email\s*compl/i.test(stepHaystack(s)));
  if (compl) return compl;
  return cand.find((s) => /content\s*ae/i.test(stepHaystack(s)));
}

function getExportFormat(config) {
  return String(
    config?.selectedFormat ?? config?.data?.selectedFormat ?? ''
  )
    .toLowerCase()
    .trim();
}

/**
 * Picks workflow outputs for Word export by format (and safe fallbacks when format is missing).
 * - email / news: Separata-named node (if any) → Newsletter → e-mail post-compliance
 * - separata: Content Creator MIA → separata-named node; else first process step
 * - whatsapp: Newsletter Creator → “text to whatsapp … HCP”
 */
export function collectPrincipalWordExportSections(steps, allStepContents, config = {}) {
  if (!steps?.length) return [];

  const format = getExportFormat(config);

  let orderedSteps = [];

  if (format === 'separata') {
    const c = pickContentCreator(steps);
    const sep = pickSeparata(steps);
    orderedSteps = dedupeStepsById([c, sep]);
    if (orderedSteps.length === 0) {
      const fp = pickFirstProcessStep(steps);
      if (fp) orderedSteps = [fp];
    }
  } else if (format === 'whatsapp') {
    orderedSteps = dedupeStepsById([
      pickNewsletter(steps),
      pickWhatsappHcpMessage(steps),
    ]);
  } else if (
    !format &&
    pickContentCreator(steps) &&
    !pickNewsletter(steps)
  ) {
    const c = pickContentCreator(steps);
    const sep = pickSeparata(steps);
    orderedSteps = dedupeStepsById([c, sep]);
    if (orderedSteps.length === 0) {
      const fp = pickFirstProcessStep(steps);
      if (fp) orderedSteps = [fp];
    }
  } else if (!format && pickWhatsappHcpMessage(steps)) {
    orderedSteps = dedupeStepsById([
      pickNewsletter(steps),
      pickWhatsappHcpMessage(steps),
    ]);
  } else {
    orderedSteps = dedupeStepsById([
      pickSeparata(steps),
      pickNewsletter(steps),
      pickEmailPostCompliance(steps),
    ]);
  }

  return orderedSteps
    .map((step) => ({
      id: step.id,
      label: step.label || step.id,
      raw: getStepRaw(step, allStepContents),
    }))
    .filter((s) => s.raw);
}

const MD_IMG_RE = /!\[([^\]]*)\]\(([^)\s]+)\)/g;

/** Space after paragraphs (twips), similar to blank lines in the in-app copy. */
const SP_AFTER_LINE = 80;
const SP_AFTER_BLOCK = 220;
const SP_AFTER_P = 180;
const SP_AFTER_LIST_ITEM = 100;

function looksLikeHtmlFragment(s) {
  return /<(?:html|body|head|table|tbody|thead|tr|td|th|div|p|meta|style|span|a|img|ul|ol|li|h[1-6]|br)\b/i.test(s);
}

function splitMarkdownImages(raw) {
  const parts = [];
  let last = 0;
  let m;
  MD_IMG_RE.lastIndex = 0;
  while ((m = MD_IMG_RE.exec(raw)) !== null) {
    if (m.index > last) {
      parts.push({ kind: 'text', value: raw.slice(last, m.index) });
    }
    parts.push({ kind: 'img', src: m[2], alt: m[1] || '' });
    last = m.index + m[0].length;
  }
  if (last < raw.length) {
    parts.push({ kind: 'text', value: raw.slice(last) });
  }
  if (!parts.length) {
    parts.push({ kind: 'text', value: raw });
  }
  return parts;
}

async function blobToImageRun(blob, altText) {
  const buf = await blob.arrayBuffer();
  const type = (blob.type || '').toLowerCase();
  let imageType = 'png';
  if (type.includes('jpeg') || type.includes('jpg')) imageType = 'jpg';
  else if (type.includes('gif')) imageType = 'gif';
  else if (type.includes('bmp')) imageType = 'bmp';
  else if (type.includes('png')) imageType = 'png';
  else if (!type.includes('webp')) {
    const sig = new Uint8Array(buf.slice(0, 4));
    if (sig[0] === 0xff && sig[1] === 0xd8) imageType = 'jpg';
    else if (sig[0] === 0x89 && sig[1] === 0x50) imageType = 'png';
    else if (sig[0] === 0x47 && sig[1] === 0x49) imageType = 'gif';
  } else {
    return null;
  }

  const dims = await measureImageDimensions(buf, imageType);
  return new ImageRun({
    type: imageType,
    data: buf,
    transformation: {
      width: dims.width,
      height: dims.height,
    },
    altText: altText
      ? { name: 'Image', description: altText.slice(0, 240) }
      : { name: 'Image' },
  });
}

function measureImageDimensions(buffer, imageType) {
  return new Promise((resolve) => {
    try {
      const mime =
        imageType === 'jpg'
          ? 'image/jpeg'
          : imageType === 'gif'
            ? 'image/gif'
            : imageType === 'bmp'
              ? 'image/bmp'
              : 'image/png';
      const blob = new Blob([buffer], { type: mime });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 400;
        const h = img.naturalHeight || 300;
        URL.revokeObjectURL(url);
        const scale = w > MAX_IMAGE_WIDTH_PX ? MAX_IMAGE_WIDTH_PX / w : 1;
        resolve({
          width: Math.max(1, Math.round(w * scale)),
          height: Math.max(1, Math.round(h * scale)),
        });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 400, height: 280 });
      };
      img.src = url;
    } catch {
      resolve({ width: 400, height: 280 });
    }
  });
}

async function fetchImageRun(src, alt) {
  if (!src?.trim()) return null;
  try {
    const res = await fetch(src.trim(), { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blobToImageRun(blob, alt || '');
  } catch {
    return null;
  }
}

async function imageParagraphFromUrl(src, alt) {
  const run = await fetchImageRun(src, alt);
  if (run) {
    return new Paragraph({
      children: [run],
      alignment: AlignmentType.CENTER,
      spacing: { after: SP_AFTER_BLOCK },
    });
  }
  return new Paragraph({
    children: [
      new TextRun({ text: 'Image (could not embed): ', italics: true }),
      new TextRun({ text: src, color: '0563C1' }),
    ],
    spacing: { after: SP_AFTER_P },
  });
}

function parseHtmlInBrowser(html) {
  const trimmed = html.trim();
  if (/^<!DOCTYPE|^<html\b/i.test(trimmed)) {
    const doc = new DOMParser().parseFromString(trimmed, 'text/html');
    return doc.body;
  }
  const doc = new DOMParser().parseFromString(
    `<div id="mia-docx-root">${trimmed}</div>`,
    'text/html'
  );
  return doc.getElementById('mia-docx-root');
}

const HEADING_MAP = {
  h1: HeadingLevel.HEADING_1,
  h2: HeadingLevel.HEADING_2,
  h3: HeadingLevel.HEADING_3,
  h4: HeadingLevel.HEADING_4,
  h5: HeadingLevel.HEADING_5,
  h6: HeadingLevel.HEADING_6,
};

async function flattenInlineNodes(node, fetchImageFn) {
  /** @type {import('docx').ParagraphChild[]} */
  const runs = [];

  async function walk(n, st = { bold: false, italics: false, underline: undefined }) {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n.textContent.replace(/\u00a0/g, ' ');
      if (t) {
        runs.push(
          new TextRun({
            text: t,
            bold: st.bold || undefined,
            italics: st.italics || undefined,
            underline: st.underline,
          })
        );
      }
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;

    const el = n;
    const tag = el.tagName.toLowerCase();

    if (tag === 'br') {
      runs.push(new TextRun({ break: 1 }));
      return;
    }

    if (tag === 'strong' || tag === 'b') {
      for (const c of el.childNodes) await walk(c, { ...st, bold: true });
      return;
    }

    if (tag === 'em' || tag === 'i') {
      for (const c of el.childNodes) await walk(c, { ...st, italics: true });
      return;
    }

    if (tag === 'u') {
      for (const c of el.childNodes) {
        await walk(c, { ...st, underline: { type: 'single' } });
      }
      return;
    }

    if (tag === 'a') {
      const href = el.getAttribute('href') || '';
      const label = (el.textContent || href || 'link').trim();
      if (href) {
        runs.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: label,
                style: 'Hyperlink',
                color: '0563C1',
                underline: { type: 'single' },
              }),
            ],
            link: href,
          })
        );
      } else if (label) {
        runs.push(new TextRun({ text: label, bold: st.bold || undefined, italics: st.italics || undefined }));
      }
      return;
    }

    if (tag === 'img') {
      const src = el.getAttribute('src') || '';
      const alt = el.getAttribute('alt') || '';
      const run = await fetchImageFn(src, alt);
      if (run) runs.push(run);
      return;
    }

    if (tag === 'span' || tag === 'small' || tag === 'font' || tag === 'code') {
      for (const c of el.childNodes) await walk(c, st);
      return;
    }

    for (const c of el.childNodes) await walk(c, st);
  }

  for (const c of node.childNodes) await walk(c);
  return runs;
}

const HTML_SOURCE_CHUNK = 7500;

/**
 * @param {string} s
 * @returns {string | null} Full document starting at <!DOCTYPE html> or <html>, or null
 */
function extractFullHtmlDocument(s) {
  const t = s.trim();
  if (/^<!DOCTYPE\s+html/i.test(t) || /^<html\b/i.test(t)) {
    return t;
  }
  const idxDoctype = t.search(/<!DOCTYPE\s+html/i);
  const idxHtml = t.search(/<html\b/i);
  const idx = idxDoctype >= 0 ? idxDoctype : idxHtml;
  if (idx >= 0) return t.slice(idx);
  return null;
}

/**
 * Raw HTML as monospace paragraphs (no title line — starts with user markup e.g. <!DOCTYPE html>).
 * @param {string} html
 * @returns {import('docx').Paragraph[]}
 */
function rawHtmlToSourceParagraphs(html) {
  const text = String(html).replace(/\r\n/g, '\n');
  /** @type {import('docx').Paragraph[]} */
  const paras = [];

  for (let i = 0; i < text.length; i += HTML_SOURCE_CHUNK) {
    paras.push(
      new Paragraph({
        children: [
          new TextRun({
            text: text.slice(i, i + HTML_SOURCE_CHUNK),
            font: 'Consolas',
            size: 18,
          }),
        ],
        shading: {
          fill: 'F4F6F9',
          type: ShadingType.CLEAR,
        },
        spacing: { after: 40 },
      })
    );
  }

  paras.push(new Paragraph({ text: '' }));
  return paras;
}

/**
 * Word table conversion from email HTML looks poor; emit the table markup as monospace source.
 * @param {HTMLTableElement} tableEl
 * @returns {import('docx').Paragraph[]}
 */
function tableElementToHtmlSourceParagraphs(tableEl) {
  const html = (tableEl.outerHTML || '')
    .replace(/\r\n/g, '\n')
    .replace(/>\s*</g, '>\n<')
    .trim();
  return rawHtmlToSourceParagraphs(html);
}

/**
 * @param {ParentNode} root
 * @param {(src: string, alt: string) => Promise<import('docx').ImageRun | null>} fetchImageFn
 */
async function htmlNodesToDocxBlocks(root, fetchImageFn, opts = {}) {
  /** @type {import('docx').FileChild[]} */
  const blocks = [];
  const { inTableCell = false } = opts;

  async function processChild(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = (node.textContent || '').replace(/\u00a0/g, ' ');
      const lines = raw.split(/\n/);
      if (lines.length <= 1) {
        const t = raw.replace(/[ \t]+/g, ' ').trim();
        if (t) {
          blocks.push(
            new Paragraph({
              children: [new TextRun(t)],
              spacing: { after: SP_AFTER_LINE },
            })
          );
        }
        return;
      }
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].replace(/[ \t]+/g, ' ').trimEnd();
        if (line === '') {
          blocks.push(new Paragraph({ text: '', spacing: { after: SP_AFTER_LINE } }));
        } else {
          blocks.push(
            new Paragraph({
              children: [new TextRun(line)],
              spacing: { after: i === lines.length - 1 ? SP_AFTER_BLOCK : SP_AFTER_LINE },
            })
          );
        }
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    const tag = el.tagName.toLowerCase();

    if (['script', 'style', 'meta', 'link', 'title', 'noscript', 'svg'].includes(tag)) {
      return;
    }

    if (tag === 'table') {
      blocks.push(...tableElementToHtmlSourceParagraphs(el));
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      for (const li of el.querySelectorAll(':scope > li')) {
        const runs = await flattenInlineNodes(li, fetchImageFn);
        blocks.push(
          new Paragraph({
            children: runs.length ? runs : [new TextRun('')],
            bullet: { level: 0 },
            spacing: { after: SP_AFTER_LIST_ITEM },
          })
        );
      }
      return;
    }

    if (HEADING_MAP[tag]) {
      const runs = await flattenInlineNodes(el, fetchImageFn);
      blocks.push(
        new Paragraph({
          children: runs.length ? runs : [new TextRun(el.textContent?.trim() || '')],
          heading: HEADING_MAP[tag],
          spacing: { after: SP_AFTER_BLOCK },
        })
      );
      return;
    }

    if (tag === 'p' || tag === 'blockquote') {
      const runs = await flattenInlineNodes(el, fetchImageFn);
      if (runs.length) {
        blocks.push(
          new Paragraph({
            children: runs,
            spacing: { after: SP_AFTER_P },
          })
        );
      }
      return;
    }

    if (tag === 'img') {
      const src = el.getAttribute('src') || '';
      const alt = el.getAttribute('alt') || '';
      blocks.push(await imageParagraphFromUrl(src, alt));
      return;
    }

    if (tag === 'hr') {
      blocks.push(
        new Paragraph({ text: '— — —', spacing: { after: SP_AFTER_BLOCK } })
      );
      return;
    }

    if (tag === 'pre') {
      const text = el.textContent || '';
      const lines = text.split('\n');
      lines.forEach((line, idx) => {
        blocks.push(
          new Paragraph({
            children: [new TextRun({ text: line || ' ', font: 'Consolas', size: 22 })],
            spacing: {
              after: idx === lines.length - 1 ? SP_AFTER_BLOCK : SP_AFTER_LINE,
            },
          })
        );
      });
      return;
    }

    if (['div', 'section', 'article', 'main', 'center', 'body', 'header', 'footer', 'nav', 'form', 'li'].includes(tag)) {
      for (const c of el.childNodes) await processChild(c);
      return;
    }

    if (tag === 'br') {
      blocks.push(new Paragraph({ text: '', spacing: { after: SP_AFTER_LINE } }));
      return;
    }

    if (!inTableCell && (tag === 'tr' || tag === 'td' || tag === 'th' || tag === 'tbody' || tag === 'thead')) {
      for (const c of el.childNodes) await processChild(c);
      return;
    }

    const runs = await flattenInlineNodes(el, fetchImageFn);
    if (runs.length) {
      blocks.push(
        new Paragraph({
          children: runs,
          spacing: { after: SP_AFTER_P },
        })
      );
    } else {
      for (const c of el.childNodes) await processChild(c);
    }
  }

  for (const c of root.childNodes) await processChild(c);
  return blocks;
}

async function htmlStringToDocxBlocks(htmlString, fetchImageFn) {
  const fullDoc = extractFullHtmlDocument(htmlString);
  if (fullDoc) {
    return rawHtmlToSourceParagraphs(fullDoc);
  }
  const root = parseHtmlInBrowser(htmlString);
  if (!root) return [new Paragraph({ text: '(Unable to parse HTML.)' })];
  return htmlNodesToDocxBlocks(root, fetchImageFn);
}

function configureMarked() {
  marked.setOptions({ gfm: true, breaks: true });
}

/** When false, treat as prose with intentional newlines (copy-style); when true, parse as Markdown → HTML. */
function stringLooksLikeMarkdown(s) {
  const t = String(s);
  return (
    /(^|\n)#{1,6}\s/.test(t) ||
    /(^|\n)\s*[-*+]\s/.test(t) ||
    /(^|\n)\s*\d+\.\s/.test(t) ||
    /\*\*[^*\s]/.test(t) ||
    /!\[[^\]]*\]\([^)]+\)/.test(t) ||
    /\[[^\]]+\]\([^)]+\)/.test(t) ||
    /(^|\n)>[\s>]/.test(t)
  );
}

/**
 * Plain text (no HTML tags): mirrors line breaks / blank lines from the in-app copy.
 */
function plainNewlinesToParagraphs(text) {
  const norm = String(text).replace(/\r\n/g, '\n').replace(/\u00a0/g, ' ');
  const blocks = norm.split(/\n{2,}/);
  /** @type {import('docx').Paragraph[]} */
  const out = [];

  for (let bi = 0; bi < blocks.length; bi++) {
    const block = blocks[bi];
    const lines = block.split('\n');
    const isLastBlock = bi === blocks.length - 1;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].replace(/[ \t]+/g, ' ').trimEnd();
      const isLastLine = i === lines.length - 1;

      if (line === '') {
        out.push(
          new Paragraph({
            text: '',
            spacing: { after: isLastLine && isLastBlock ? 0 : SP_AFTER_LINE },
          })
        );
        continue;
      }

      out.push(
        new Paragraph({
          children: [new TextRun(line)],
          spacing: {
            after: isLastLine ? (isLastBlock ? 0 : SP_AFTER_BLOCK) : SP_AFTER_LINE,
          },
        })
      );
    }
  }

  return out;
}

async function textChunkToDocxBlocks(chunk, fetchImageFn) {
  const trimmed = chunk.trim();
  if (!trimmed) return [];

  const fullDoc = extractFullHtmlDocument(trimmed);
  if (fullDoc) {
    return rawHtmlToSourceParagraphs(fullDoc);
  }

  if (looksLikeHtmlFragment(trimmed)) {
    return htmlStringToDocxBlocks(trimmed, fetchImageFn);
  }

  if (!trimmed.includes('<') && !stringLooksLikeMarkdown(trimmed)) {
    return plainNewlinesToParagraphs(trimmed);
  }

  configureMarked();
  const asHtml = marked.parse(trimmed, { async: false });
  const html = typeof asHtml === 'string' ? asHtml : String(asHtml);
  return htmlStringToDocxBlocks(html, fetchImageFn);
}

async function rawStepContentToDocxBlocks(raw, fetchImageFn) {
  const parts = splitMarkdownImages(raw);
  /** @type {import('docx').FileChild[]} */
  const all = [];

  for (const part of parts) {
    if (part.kind === 'img') {
      all.push(await imageParagraphFromUrl(part.src, part.alt));
    } else {
      all.push(...(await textChunkToDocxBlocks(part.value, fetchImageFn)));
    }
  }

  return all.length ? all : [new Paragraph({ text: '(No content for this step.)' })];
}

/**
 * Word export: sections depend on workflow format (see collectPrincipalWordExportSections).
 */
export async function buildWorkflowFullDocxBlob({ steps, allStepContents, config }) {
  const sectionData = collectPrincipalWordExportSections(steps, allStepContents, config);
  if (sectionData.length === 0) {
    throw new Error('No principal step content to export');
  }

  const fetchImageFn = (src, alt) => fetchImageRun(src, alt);

  const children = [];

  children.push(
    new Paragraph({
      text: 'MIA Content - Full Workflow Results',
      heading: HeadingLevel.TITLE,
      spacing: { after: SP_AFTER_BLOCK },
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated ${new Date().toLocaleString()}`,
          italics: true,
        }),
      ],
      spacing: { after: SP_AFTER_LINE },
    })
  );

  const metaParts = [
    config?.selectedFormatLabel || config?.data?.selectedFormatLabel,
    config?.bayerBrand,
    config?.brand || config?.data?.brand || config?.product || config?.data?.product,
    config?.outputLanguage || config?.data?.outputLanguage,
  ].filter(Boolean);

  if (metaParts.length) {
    children.push(
      new Paragraph({
        text: metaParts.join(' · '),
        spacing: { after: SP_AFTER_BLOCK },
      })
    );
  }

  children.push(new Paragraph({ text: '', spacing: { after: SP_AFTER_LINE } }));

  for (const { label, raw } of sectionData) {
    children.push(
      new Paragraph({
        text: label,
        heading: HeadingLevel.HEADING_2,
        spacing: { after: SP_AFTER_LINE },
      })
    );

    const bodyBlocks = await rawStepContentToDocxBlocks(raw, fetchImageFn);
    children.push(...bodyBlocks);
    children.push(
      new Paragraph({ text: '', spacing: { after: SP_AFTER_BLOCK } })
    );
  }

  const doc = new Document({
    sections: [{ children }],
  });

  return Packer.toBlob(doc);
}

/**
 * Word export for a single workflow step (any node with stored output).
 */
export async function buildSingleStepDocxBlob({ raw, stepLabel, config }) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    throw new Error('No content to export for this step');
  }

  const fetchImageFn = (src, alt) => fetchImageRun(src, alt);
  const children = [];

  children.push(
    new Paragraph({
      text: 'MIA Content - Step Result',
      heading: HeadingLevel.TITLE,
      spacing: { after: SP_AFTER_BLOCK },
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `${stepLabel || 'Workflow step'} · ${new Date().toLocaleString()}`,
          italics: true,
        }),
      ],
      spacing: { after: SP_AFTER_LINE },
    })
  );

  const metaParts = [
    config?.selectedFormatLabel || config?.data?.selectedFormatLabel,
    config?.bayerBrand,
    config?.brand || config?.data?.brand || config?.product || config?.data?.product,
    config?.outputLanguage || config?.data?.outputLanguage,
  ].filter(Boolean);

  if (metaParts.length) {
    children.push(
      new Paragraph({
        text: metaParts.join(' · '),
        spacing: { after: SP_AFTER_BLOCK },
      })
    );
  }

  children.push(new Paragraph({ text: '', spacing: { after: SP_AFTER_LINE } }));

  children.push(
    new Paragraph({
      text: stepLabel || 'Output',
      heading: HeadingLevel.HEADING_2,
      spacing: { after: SP_AFTER_LINE },
    })
  );

  const bodyBlocks = await rawStepContentToDocxBlocks(trimmed, fetchImageFn);
  children.push(...bodyBlocks);

  return Packer.toBlob(
    new Document({
      sections: [{ children }],
    })
  );
}
